import React, { useState, useEffect } from 'react';
import { useAuth } from './contexts/AuthContext';
import { toast } from 'react-toastify';
import { getAuth, updatePassword, reauthenticateWithCredential, EmailAuthProvider } from 'firebase/auth';
import { API_URL } from './config/api';
import { logger } from './utils/logger';

const Profile = ({ setCurrentPage }) => {
  const { currentUser, updateUserProfile, refreshUserStatus } = useAuth();
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [newUsername, setNewUsername] = useState(currentUser?.displayName || '');
  const [userStatus, setUserStatus] = useState(null);
  const [trialLoading, setTrialLoading] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordError, setPasswordError] = useState(null);
  
  // Animation effect when component mounts
  useEffect(() => {
    if (document.getElementById('profile-container')) {
      setTimeout(() => {
        document.getElementById('profile-container').classList.add('opacity-100', 'translate-y-0');
      }, 100);
    }

    // Fetch user status
    const fetchUserStatus = async () => {
      try {
        const response = await fetch(`${API_URL}/users/${currentUser.uid}/status`, {
          method: 'GET',
          headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
            'Origin': window.location.origin
          },
          credentials: 'include'
        });
        const data = await response.json();
        setUserStatus(data);
      } catch (error) {
        logger('Error fetching user status:', error);
      }
    };

    if (currentUser) {
      fetchUserStatus();
    }
  }, [currentUser]);

  // Helper function to format date
  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Helper function to get status badge
  const getStatusBadge = () => {
    if (!userStatus) return null;

    if (userStatus.isPremium) {
      return (
        <span className="badge badge-primary py-3 px-4 font-medium">
          Premium Member
        </span>
      );
    } else if (userStatus.isTrialActive) {
      const daysLeft = Math.ceil((new Date(userStatus.trialEndDate) - new Date()) / (1000 * 60 * 60 * 24));
      return (
        <div className="flex flex-col items-end gap-1">
          <span className="badge badge-secondary py-3 px-4 font-medium">
            Trial Active
          </span>
          <span className="text-xs text-base-content/60">
            {daysLeft} days remaining
          </span>
        </div>
      );
    } else if (userStatus.hasUsedTrial) {
      return (
        <span className="badge badge-ghost py-3 px-4 font-medium">
          Trial Expired
        </span>
      );
    } else {
      return (
        <span className="badge badge-ghost py-3 px-4 font-medium">
          Free User
        </span>
      );
    }
  };

  const handleUpdateUsername = async (e) => {
    e.preventDefault();
    if (!newUsername.trim()) {
      toast.error('Username cannot be empty');
      return;
    }

    try {
      setLoading(true);
      await updateUserProfile({ displayName: newUsername.trim() });
      toast.success('Username updated successfully!');
      setIsEditing(false);
    } catch (error) {
      toast.error(error.message || 'Failed to update username');
    } finally {
      setLoading(false);
    }
  };

  const handleStartTrial = async () => {
    try {
      setTrialLoading(true);
      const idToken = await currentUser.getIdToken();
      const response = await fetch(`${API_URL}/users/start-trial`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'Origin': window.location.origin
        },
        credentials: 'include',
        body: JSON.stringify({
          userId: currentUser.uid,
          email: currentUser.email,
          displayName: currentUser.displayName || currentUser.email.split('@')[0]
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || data.message || 'Failed to start trial');
      }

      // Refresh user status
      await refreshUserStatus();
      const newStatus = await fetch(`${API_URL}/users/${currentUser.uid}/status`, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
          'Origin': window.location.origin
        },
        credentials: 'include'
      });
      const newStatusData = await newStatus.json();
      setUserStatus(newStatusData);
      
      toast.success('Free trial activated successfully!');
      setCurrentPage('analytics');
    } catch (error) {
      logger('Error starting trial:', error);
      toast.error(error.message || 'Failed to start trial. Please try again.');
    } finally {
      setTrialLoading(false);
    }
  };

  const validatePassword = (password) => {
    if (!password) {
      setPasswordError(null);
      return false;
    }
    
    const hasMinLength = password.length >= 6;
    const hasUppercase = /[A-Z]/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    const hasNumber = /\d/.test(password);
    
    if (!hasMinLength || !hasUppercase || !hasSpecialChar || !hasNumber) {
      setPasswordError({
        hasMinLength,
        hasUppercase,
        hasSpecialChar,
        hasNumber
      });
      return false;
    }
    
    setPasswordError(null);
    return true;
  };

  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (!passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword) {
      toast.error('Please fill in all password fields');
      return;
    }

    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }

    if (!validatePassword(passwordData.newPassword)) {
      toast.error('New password does not meet requirements');
      return;
    }

    try {
      setLoading(true);
      const auth = getAuth();
      
      // Re-authenticate user
      const credential = EmailAuthProvider.credential(
        currentUser.email,
        passwordData.currentPassword
      );
      await reauthenticateWithCredential(currentUser, credential);
      
      // Update password
      await updatePassword(currentUser, passwordData.newPassword);
      
      toast.success('Password updated successfully!');
      setIsChangingPassword(false);
      setPasswordData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (error) {
      logger('Error updating password:', error);
      if (error.code === 'auth/wrong-password') {
        toast.error('Current password is incorrect');
      } else {
        toast.error(error.message || 'Failed to update password');
      }
    } finally {
      setLoading(false);
    }
  };

  if (!currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-base-100 pt-32">
        <div className="w-full max-w-md px-6">
          <div className="card bg-base-100 shadow-xl backdrop-blur-sm border border-base-300 overflow-hidden">
            <div className="card-body p-8">
              <div className="flex items-center gap-6 mb-8 animate-pulse">
                <div className="skeleton h-20 w-20 rounded-full"></div>
                <div className="flex flex-col gap-2 flex-1">
                  <div className="skeleton h-5 w-1/2"></div>
                  <div className="skeleton h-4 w-3/4"></div>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="skeleton h-24 w-full rounded-xl"></div>
                <div className="skeleton h-24 w-full rounded-xl"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-base-100 pt-32 pb-12 px-6">
      <div 
        id="profile-container"
        className="max-w-2xl mx-auto transform transition-all duration-700 opacity-0 translate-y-4"
      >
        <div className="card bg-base-100 shadow-xl backdrop-blur-sm border border-base-300 overflow-hidden mb-6">
          <div className="card-body p-8">
            {/* Profile Header */}
            <div className="flex flex-col md:flex-row md:items-center gap-6 mb-8">
              <div className="avatar">
              <div className="w-24 h-24 bg-primary/15 rounded-full flex items-center justify-center text-center text-3xl font-light ring-2 ring-primary/20 ring-offset-2 ring-offset-base-100">
                <div className="mt-7 text-[64px]">
                  {currentUser.displayName ? 
                    currentUser.displayName[0].toUpperCase() : 
                    currentUser.email.split('@')[0][0].toUpperCase()}
                </div>
              </div>

              </div>
              <div className="flex-1 flex flex-col md:flex-row md:items-end md:justify-between">
                <div>
                  <h2 className="text-2xl font-light">
                    {currentUser.displayName || currentUser.email.split('@')[0]}
                  </h2>
                  <p className="text-base-content/60 text-sm">{currentUser.email}</p>
                </div>
                <div className="mt-4 md:mt-0">
                  {getStatusBadge()}
                </div>
              </div>
            </div>

            {/* Subscription Details Card */}
            {userStatus && (
              <div className="card bg-base-200/50 rounded-xl border border-base-300/50 mb-6">
                <div className="card-body p-6">
                  <h3 className="text-sm font-medium uppercase tracking-wider text-base-content/70 mb-3">
                    Subscription Details
                  </h3>
                  <div className="space-y-2">
                    {userStatus.isPremium && (
                      <p className="text-sm">
                        Premium member since {formatDate(userStatus.premiumSince)}
                      </p>
                    )}
                    {userStatus.isTrialActive && (
                      <>
                        <p className="text-sm">
                          Trial started on {formatDate(userStatus.trialStartDate)}
                        </p>
                        <p className="text-sm">
                          Trial ends on {formatDate(userStatus.trialEndDate)}
                        </p>
                      </>
                    )}
                    {!userStatus.isPremium && !userStatus.isTrialActive && userStatus.hasUsedTrial && (
                      <div className="flex flex-col gap-3">
                        <p className="text-sm text-base-content/70">
                          Your trial has expired. Upgrade to premium to access all features.
                        </p>
                        <button
                          onClick={() => setCurrentPage('payment')}
                          className="btn btn-primary btn-sm rounded-lg"
                        >
                          Upgrade to Premium
                        </button>
                      </div>
                    )}
                    {!userStatus.isPremium && !userStatus.isTrialActive && !userStatus.hasUsedTrial && (
                      <div className="flex flex-col gap-3">
                        <p className="text-sm text-base-content/70">
                          Start your 14-day free trial to access premium features.
                        </p>
                        <button
                          onClick={() => setCurrentPage('payment')}
                          className="btn btn-secondary btn-sm rounded-lg"
                        >
                          Start Free Trial
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Profile Details */}
            <div className="space-y-6">
              <div className="card bg-base-200/50 rounded-xl border border-base-300/50 hover:border-primary/20 transition-colors">
                <div className="card-body p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between">
                    <div>
                      <h3 className="text-sm font-medium uppercase tracking-wider text-base-content/70 mb-1">Username</h3>
                      {isEditing ? (
                        <form onSubmit={handleUpdateUsername} className="mt-3">
                          <div className="flex flex-col md:flex-row gap-3">
                            <div className="flex-1">
                              <input
                                type="text"
                                value={newUsername}
                                onChange={(e) => setNewUsername(e.target.value)}
                                className="w-full px-4 py-3 bg-base-100 border border-base-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                                placeholder="Enter new username"
                              />
                            </div>
                            <div className="flex gap-2">
                              <button 
                                type="submit" 
                                className="relative px-4 py-2.5 rounded-xl bg-primary text-primary-content font-medium hover:opacity-90 transition-opacity"
                                disabled={loading}
                              >
                                {loading ? (
                                  <svg className="animate-spin h-5 w-5 mx-auto" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                  </svg>
                                ) : (
                                  'Save'
                                )}
                              </button>
                              <button 
                                type="button"
                                className="px-4 py-2.5 rounded-xl bg-base-300/50 text-base-content font-medium hover:bg-base-300 transition-colors"
                                onClick={() => setIsEditing(false)}
                                disabled={loading}
                              >
                                Cancel
                              </button>
                            </div>
                          </div>
                        </form>
                      ) : (
                        <div className="flex justify-between items-center">
                          <p className="text-lg">{currentUser.displayName || 'No username set'}</p>
                          <button 
                            className="btn btn-ghost btn-sm rounded-lg hover:bg-primary/10"
                            onClick={() => setIsEditing(true)}
                          >
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="card bg-base-200/50 rounded-xl border border-base-300/50">
                <div className="card-body p-6">
                  <h3 className="text-sm font-medium uppercase tracking-wider text-base-content/70 mb-1">Email</h3>
                  <div className="flex justify-between items-center">
                    <p className="text-lg">{currentUser.email}</p>
                    <div className="badge badge-success gap-1">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
                      </svg>
                      Verified
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="card bg-base-200/50 rounded-xl border border-base-300/50">
                <div className="card-body p-6">
                  <h3 className="text-sm font-medium uppercase tracking-wider text-base-content/70 mb-1">Account Security</h3>
                  {!isChangingPassword ? (
                    <div className="mt-2">
                      <button 
                        className="btn btn-outline btn-primary btn-sm rounded-lg"
                        onClick={() => setIsChangingPassword(true)}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                        </svg>
                        Change Password
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handlePasswordChange} className="mt-4 space-y-4">
                      <div>
                        <input
                          type="password"
                          placeholder="Current Password"
                          value={passwordData.currentPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, currentPassword: e.target.value }))}
                          className="w-full px-4 py-2 bg-base-100 border border-base-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                      <div>
                        <input
                          type="password"
                          placeholder="New Password"
                          value={passwordData.newPassword}
                          onChange={(e) => {
                            setPasswordData(prev => ({ ...prev, newPassword: e.target.value }));
                            validatePassword(e.target.value);
                          }}
                          className="w-full px-4 py-2 bg-base-100 border border-base-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>
                      <div>
                        <input
                          type="password"
                          placeholder="Confirm New Password"
                          value={passwordData.confirmPassword}
                          onChange={(e) => setPasswordData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                          className="w-full px-4 py-2 bg-base-100 border border-base-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary"
                        />
                      </div>

                      {/* Password requirements */}
                      {passwordError && (
                        <div className="text-xs text-base-content/60 px-1">
                          <p>Password must have:</p>
                          <ul className="pl-4 mt-1 space-y-0.5">
                            <li className={`flex items-center gap-1 ${passwordError.hasMinLength === false ? 'text-error/80' : passwordError.hasMinLength === true ? 'text-success/80' : ''}`}>
                              <span className="inline-block w-1 h-1 rounded-full bg-current"></span> At least 6 characters
                            </li>
                            <li className={`flex items-center gap-1 ${passwordError.hasUppercase === false ? 'text-error/80' : passwordError.hasUppercase === true ? 'text-success/80' : ''}`}>
                              <span className="inline-block w-1 h-1 rounded-full bg-current"></span> One uppercase letter
                            </li>
                            <li className={`flex items-center gap-1 ${passwordError.hasNumber === false ? 'text-error/80' : passwordError.hasNumber === true ? 'text-success/80' : ''}`}>
                              <span className="inline-block w-1 h-1 rounded-full bg-current"></span> One number
                            </li>
                            <li className={`flex items-center gap-1 ${passwordError.hasSpecialChar === false ? 'text-error/80' : passwordError.hasSpecialChar === true ? 'text-success/80' : ''}`}>
                              <span className="inline-block w-1 h-1 rounded-full bg-current"></span> One special character
                            </li>
                          </ul>
                        </div>
                      )}

                      <div className="flex gap-2 justify-end">
                        <button
                          type="button"
                          onClick={() => {
                            setIsChangingPassword(false);
                            setPasswordData({
                              currentPassword: '',
                              newPassword: '',
                              confirmPassword: ''
                            });
                            setPasswordError(null);
                          }}
                          className="btn btn-ghost btn-sm rounded-lg"
                          disabled={loading}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn btn-primary btn-sm rounded-lg"
                          disabled={loading}
                        >
                          {loading ? (
                            <>
                              <span className="loading loading-spinner loading-sm"></span>
                              Updating...
                            </>
                          ) : (
                            'Update Password'
                          )}
                        </button>
                      </div>
                    </form>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="flex justify-center">
          <button 
            onClick={() => setCurrentPage('home')}
            className="btn btn-outline rounded-xl btn-sm"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Home
          </button>
        </div>
      </div>
    </div>
  );
};

export default Profile; 